import React, { ReactNode, CSSProperties, useState } from "react";
import styles from './_dialog-provider.module.scss';
import { 
  Dialog, PaperProps, Paper
} from "@mui/material";

import { Draggable } from '@progress/kendo-react-common';

type ProviderContext = readonly [(option: DialogOption) => void, () => void];

const noop = () => {};
const DialogContext = React.createContext<ProviderContext>([
  noop,
  noop
]);


export const useDialog = () => React.useContext(DialogContext);

type DialogParams = {
  children: React.ReactNode;
  open: boolean;
  onClose?: Function;
  onExited?: Function;
  fullScreen? : boolean;
  style?: CSSProperties
};
export type DialogOption = Omit<DialogParams, "open">;
type DialogContainerProps = DialogParams & {
  onClose: () => void;
  onKill: () => void;  
};



function PaperComponent(props: PaperProps) {
  const [position, setPosition] = useState({ x: 0, y: 0 });

  const handleDrag = (e: any) => {
    setPosition({
      x: position.x + e.deltaX,
      y: position.y + e.deltaY
    });
  };

  return (
    <Draggable onDrag={handleDrag}>
      <div
        style={{
          transform: `translate(${position.x}px, ${position.y}px)`,
          maxWidth: "100vw"
        }}
      >
        <Paper {...props} />
      </div>
    </Draggable>
  );
}


function DialogContainer(props: DialogContainerProps) {
  const { children, open, onClose, onKill, fullScreen, style } = props;
  return (
    <Dialog
      className={styles.dialogProviderContainer}
      disableEscapeKeyDown={true}
      fullScreen={fullScreen}
      aria-modal={false}      
      open={open}       
      style={style}
      onClose={onClose}
      PaperComponent={PaperComponent}
      aria-labelledby="draggable-dialog-title"
    >
        {children}
    </Dialog>   
  );
}

export default function DialogProvider(props: { children: ReactNode }) {
  const [dialogs, setDialogs] = React.useState<DialogParams[]>([]);
  const createDialog = (option: DialogOption) => {
    const dialog = { ...option, open: true };
    setDialogs((dialogs) => [...dialogs, dialog]);
  };
  const closeDialog = () => {
    setDialogs((dialogs) => {
      const latestDialog = dialogs.pop();
      if (!latestDialog) return dialogs;
      if (latestDialog.onClose) latestDialog.onClose();
      return [...dialogs].concat({ ...latestDialog, open: false });
    });
  };
  const contextValue = React.useRef([createDialog, closeDialog] as const);

  return (
    <DialogContext.Provider value={contextValue.current}>
      {props.children}
      {dialogs.map((dialog, i) => {
        const { onClose, ...dialogParams } = dialog;
        const handleKill = () => {
          if (dialog.onExited) dialog.onExited();
          setDialogs((dialogs) => dialogs.slice(0, dialogs.length - 1));
        };

        return (
          <DialogContainer
            key={i}
            onClose={closeDialog}
            onKill={handleKill}
            {...dialogParams}
          />
        );
      })}
    </DialogContext.Provider>
  );
}
